<template>
  <mu-card id="activity-input">

    <mu-card-text class="card-style">
      <div v-if="loading === true">
        <mu-flexbox class="card-loading-padding" align="center" justify="center">
          <mu-flexbox-item align="center">
            <mu-circular-progress :size="56" color="#FFFFFF" :strokeWidth="5"/>
          </mu-flexbox-item>
        </mu-flexbox>
      </div>

      <mu-flexbox v-if="!loading && data.error" class="card-error-padding" justify="center">
        <mu-flexbox-item align="center">
          <div class="card-notice-title" v-text="$t('message.cardEmpty')"></div>
          <div>{{$t('message.cardTapPrefix')}}
            <mu-icon v-if="data.no_setting" class="card-icon-error" :size="16" slot="left" value="settings"></mu-icon>
            <mu-icon v-if="data.error" class="card-icon-error" :size="16" slot="left" value="add"></mu-icon>
            <span v-if="data.error || data.no_setting">{{ $t('message.cardTapSuffix') }}</span>
          </div>
        </mu-flexbox-item>
      </mu-flexbox>

      <div v-if="!loading && !data.error" class="animated fadeIn">
        <div @click="goToDetail" style="cursor: pointer">
          <span v-show="false">{{hasData = true}}</span>

          <div v-if="hasStepsAndNoSelfReportedReadings || isStepsMoreRecent">
            <mu-content-block>
              <mu-flexbox>
                <mu-flexbox-item class="card-lite-title">
                  {{$t('message.labelTotalSteps')}}
                </mu-flexbox-item>
                <mu-flexbox-item>
                <div class="card-large-stat" align="right">
                  <span v-text="formatNumber(data.steps.steps)"></span>
                  <span v-tooltip.top="'Todays estimated steps'" style="margin-left: -3px" v-text="'/' + formatNumber(data.steps.predictions.steps)"></span>
                </div>
              </mu-flexbox-item>
              </mu-flexbox>
            </mu-content-block>


            <mu-content-block>
              <mu-flexbox>
                <mu-flexbox-item class="card-lite-title">
                  {{$t('message.labelFromDevice')}}
                </mu-flexbox-item>
                <mu-flexbox-item>
                  <div class="card-large-stat" align="right">
                    <span v-text="deviceName"></span>
                  </div>
                </mu-flexbox-item>
              </mu-flexbox>
            </mu-content-block>


            <mu-content-block v-if="data.steps.distance_mi !== null">
              <mu-flexbox>
                <mu-flexbox-item class="card-lite-title">
                  <!-- -->
                  {{$t('message.labelDistance')}}
                </mu-flexbox-item>
                <mu-flexbox-item>
                  <div align="right">
                    <span v-text="data.steps.distance_mi.toFixed(2) + ' mi'"></span>
                  </div>
                </mu-flexbox-item>
              </mu-flexbox>
            </mu-content-block>


            <mu-content-block>
              <mu-flexbox>
                <mu-flexbox-item class="card-lite-title">
                  <!-- -->
                  {{$t('message.labelFloorsClimbed')}}
                </mu-flexbox-item>
                <mu-flexbox-item>
                  <div align="right">
                    <span v-text="data.steps.floors"></span>
                  </div>
                </mu-flexbox-item>
              </mu-flexbox>
            </mu-content-block>


            <mu-content-block>
              <mu-flexbox>
                <mu-flexbox-item class="card-lite-title">
                  <!-- -->
                  {{$t('message.labelCaloriesBurnt')}}
                </mu-flexbox-item>
                <mu-flexbox-item>
                  <div align="right">
                    <span v-text="formatNumber(data.steps.calories) + ' kcal'"></span>
                  </div>
                </mu-flexbox-item>
              </mu-flexbox>
            </mu-content-block>


          </div>


          <div v-else>

            <mu-content-block>
              <mu-flexbox>
                <mu-flexbox-item class="card-lite-title">
                  {{$t('message.labelActivity')}}
                </mu-flexbox-item>
                <mu-flexbox-item>
                  <div class="card-large-stat" align="right">
                    <span v-text="getLocaleLabel(data.type)"></span>
                  </div>
                </mu-flexbox-item>
              </mu-flexbox>
            </mu-content-block>

            <mu-content-block>
              <mu-flexbox>
                <mu-flexbox-item class="card-lite-title">
                  <!-- -->
                  {{$t('message.labelDuration')}}
                </mu-flexbox-item>
                <mu-flexbox-item>
                  <div align="right">
                    <span v-text="data.minutes + ' mins'"></span>

                  </div>
                </mu-flexbox-item>
              </mu-flexbox>
            </mu-content-block>
            <mu-content-block align="right">
              <mu-flexbox orient="horizontal" justify="center">
                <mu-flexbox-item class="card-lite-title">
                  {{$t('message.labelCaloriesBurnt')}}
                </mu-flexbox-item>
                <mu-flexbox-item>
                  <div align="right">
                    <span v-text="data.calories_burnt + ' kcal'"></span>
                  </div>
                </mu-flexbox-item>
              </mu-flexbox>
            </mu-content-block>
          </div>


        </div>
      </div>
    </mu-card-text>

    <mu-card-actions>
      <mu-flexbox justify="flex_start" style="height: 48px">
        <mu-icon value="directions_walk" class="card-left-icon"></mu-icon>
        <mu-flexbox-item class="left16" v-text="title"></mu-flexbox-item>
        <mu-flexbox-item align="right">
          <mu-icon-button
            :disabled="loading"
            class="card-bar-icon"
            :icon="isOpenInput ? 'remove' : 'add'"
            @click.native="toggle"
          />
        </mu-flexbox-item>
      </mu-flexbox>
    </mu-card-actions>

    <transition :css="false" @enter="onToggleEnter" @leave="onToggleLeave">
      <form
        @submit.prevent="saveReading"
        v-if="isOpenInput"
        novalidate
        style="overflow: hidden"
        v-bind:style="{height: computedAuto}">
        <mu-card-text>
          <mu-select-field
            v-validate
            data-vv-validate-on="none"
            :data-vv-rules="'required'"
            data-vv-name="activityType"
            :data-vv-as="$t('message.labelActivityType')"
            data-vv-value-path="inputValue"
            :errorText="errors.first('activityType')"
            :label="$t('message.labelActivityType')"
            v-model="model.type"
            :hint-text="$t('message.selectText')"
            fullWidth>
            <mu-menu-item
              v-for="item, index in activityTypes"
              :key="'activity-' + index"
              :title="getLocaleLabel(item.value)"
              :value="item.value"
            ></mu-menu-item>
          </mu-select-field>

          <mu-text-field
            v-validate
            data-vv-validate-on="none"
            :data-vv-rules="'required'"
            data-vv-name="duration"
            :data-vv-as="$t('message.labelDurationMinutes')"
            data-vv-value-path="inputValue"
            :errorText="errors.first('duration')"
            v-model="model.minutes"
            :hintText="$t('message.placeholderText')"
            :label="$t('message.labelDurationMinutes')"
            type="number"
            fullWidth
          >
          </mu-text-field>

          <mu-checkbox v-model="model.customDate" :label="$t('message.labelCustomDate')"></mu-checkbox>
          <date-picker v-if="model.customDate" type="datetime" v-model="model.date"></date-picker>
          <div class="bottom"></div>

          <alert type="info" icon="directions_walk">
            <div slot="content">
              {{$t('message.labelCalorieBurn')}} {{calorieBurn}}kcal
            </div>
          </alert>
        </mu-card-text>


        <mu-card-actions align="right">
          <button type="submit" style="display: none;"></button>
          <mu-flat-button
            :label="$t('message.discardText')"
            @click.native="closeInput"
            class="demo-flat-button"
          />
          <mu-flat-button
            type="button"
            @click.native="saveReading"
            :disabled="isSaving"
            :label="isSaving ? $t('message.savingText'): $t('message.saveText')"
            class="demo-flat-button"
          />
        </mu-card-actions>
      </form>
    </transition>

  </mu-card>
</template>
<script type="text/babel">


  import { TweenMax, TweenLite, Power3, ScrollToPlugin } from 'gsap/all'
  import 'gsap/ScrollToPlugin'
  const plugins = [ ScrollToPlugin];

  import numeral from 'numeral'


  import ActivityApi from '../../../../api/Tracking/Activity'
  import activityTypes from '../../activityTypes'
  import DatePicker from "../../../DateTimePicker/DateTimePicker.vue";
  import CardAnimationMixin from "../../../../mixins/CardAnimationMixin"
  import Alert from "../../../Alert/Alert";
  import EventBus from '../../../../helpers/EventBus'
  import MuContentBlock from "muse-ui/src/contentBlock/contentBlock";

  export default {
    mixins: [CardAnimationMixin],
    components: {
      MuContentBlock,
      Alert,
      DatePicker
    },
    name: 'activity-input',
    props: {
      bmr: {
        type: Number,
        default: 0
      }
    },
    data() {
      return {
        isSaving: false,
        title: this.$t('message.cardActivity'),
        isOpenInput: false,
        scroller: document.getElementsByClassName('main-content')[0],
        data: {},
        model: {
          type: null,
          minutes: null,
          calories_burnt: 0
        },
        activityTypes: activityTypes,
        loading: true
      }
    },
    mounted() {
      let self = this;
      self.getData();
      EventBus.$on('activityPusherReceived', function (model) {
        self.data.progres_id = model.progress_id;
        self.data.type = model.type;
        self.data.minutes = model.minutes;
        self.data.calories_burnt = model.calories_burnt;
      });
    },
    methods: {
      formatNumber(num) {
        let number = numeral(num);
        return number.format();
      },
      getLocaleLabel(key) {
        let self = this;
        let activeKey = self.activityTypes.filter(function (item) {
          return item.value === key
        })[0];
        return self.$t(activeKey.locale);
      },
      getData() {
        let self = this;
        self.$nextTick(function () {
          if (_.has(self.$store.state.cards.activity, 'progress_id')) {
            self.loading = false;
            self.data = self.$store.state.cards.activity;
          }
          else {
            ActivityApi.getActivityTrackingCard()
              .then(function (response) {
                self.loading = false;
                self.data = response.data;
                self.$store.commit('setActivityCard', response.data);
              });
          }
        });
      },
      goToDetail() {
        let self = this;
        self.$router.push({
          name: 'ActivityProgress'
        })
      },
      saveReading() {
        let self = this;
        if (!_.has(self.model, 'date')) {
          self.model.date = moment().format();
        }
        self.$validator.validateAll()
          .then(function (valid) {
            if (valid === true) {
              self.isSaving = true;
              ActivityApi.addActivity(self.model)
                .then(function (response) {
                  self.$gtag.event('ReadingAdded',{
                    'event_category': 'ActivityCard',
                    'event_label': 'Added reading',
                    'value': 1
                  });
                  if (self.$route.name === 'Dashboard') {
                    TweenLite.to(self.scroller, 0.2, {
                      scrollTo: {
                        y: self.$el,
                        offsetY: 16
                      }
                    });
                  }
                  self.data = response.data;
                  self.isSaving = false;
                  self.$toastr.success("Activity entry added");
                  self.$store.commit('setActivityCard', response.data);
                  self.model = {
                    type: null,
                    minutes: null,
                    calories_burnt: 0
                  };
                  self.isOpenInput = false;
                }.bind(self), 300)
            }
          })
      },
      toggle() {
        let self = this;
        if (self.isOpenInput === false) {
          self.$gtag.event('Open',{
            'event_category': 'ActivityCard',
            'event_label': 'Opened card',
            'value': 0
          })
        }
        self.isOpenInput = !self.isOpenInput;
      },
      closeInput() {
        let self = this;
        self.isOpenInput = false;
        self.$gtag.event('Closed',{
          'event_category': 'ActivityCard',
          'event_label': 'Closed card',
          'value': 0
        })
      }
    },
    computed: {
      isStepsMoreRecent() {
        let self = this;
        if (self.data.steps === null) {
          return false;
        }
        return moment(self.data.steps.date_created).isAfter(moment(self.data.date_created));
      },
      hasStepsAndNoSelfReportedReadings() {
        let self = this;
        if (!_.has(self.data, 'steps')) {
          return false;
        }
        return self.data.selfReportedCount === 0 && _.has(self.data, 'steps') && _.has(self.data.steps, 'steps');
      },
      deviceName() {
        let self = this;
        switch (self.data.steps.device) {
          case 'FITBIT':
            return 'FitBit';
          default:
            return 'N/A'
        }
      },
      calorieBurn() {
        let self = this;
        let calorieBurn = 0;
        let duration = self.model.minutes;
        duration = duration /= 60.0;
        if (self.model.type !== null && self.model.minutes !== null) {
          switch (self.model.type) {
            case 'CYCLING':
            case 'SWIMMING':
            case 'GYM_ROWING':
            case 'GYM_TREADMILL':
              calorieBurn = (self.bmr / 24.0) * 8 * duration;
              break;
            case 'AEROBICS':
              calorieBurn = (self.bmr / 24.0) * 6 * duration;
              break;
            case 'GYM_CYCLING':
              calorieBurn = (self.bmr / 24.0) * 4 * duration;
              break;
            case 'RUNNING':
              calorieBurn = (self.bmr / 24.0) * 7.5 * duration;
              break;
            case 'STAIR_WALKING':
              calorieBurn = (self.bmr / 24.0) * 5 * duration;
              break;
            case 'WALKING':
              calorieBurn = (self.bmr / 24.0) * 3.8 * duration;
              break;
            case 'GYM_WEIGHTS':
              calorieBurn = (self.bmr / 24.0) * 5 * duration;
              break;
            case 'YOGA':
              calorieBurn = (self.bmr / 24.0) * 4.8 * duration;
              break;
            case 'TAI_CHI':
              calorieBurn = (self.bmr / 24.0) * 4.4 * duration;
              break;
            case 'PILATES':
              calorieBurn = (self.bmr / 24.0) * 4.9 * duration;
              break;
            case 'SPORTS':
              calorieBurn = (self.bmr / 24.0) * 5.1 * duration;
              break;
            case 'DANCING':
              calorieBurn = (self.bmr / 24.0) * 4.8 * duration;
              break;
            case 'SALSA_DANCING':
              calorieBurn = (self.bmr / 24.0) * 4 * duration;
              break;
            case 'GENERAL':
            case 'SEX':
              calorieBurn = (self.bmr / 24.0) * 2.5 * duration;
              break;
          }
          self.model.calories_burnt = calorieBurn.toFixed(0);
          return calorieBurn.toFixed(0);
        }
        else {
          return 0;
        }
      }
    },
    beforeDestroy() {
      EventBus.$off('activityPusherReceived');
    }
  }
</script>
<style lang="less">
  @import "activity.less";
</style>



// WEBPACK FOOTER //
// Activity.vue?6fc5959b